import React, { useEffect, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { PageBody, PageHeader } from "@/components/AppLayout";
import { listExpenses, markPaid } from "@/lib/api";
import StatusPill from "@/components/StatusPill";
import { useToast } from "@/hooks/use-toast";
import { REIMBURSE } from "@/constants/testIds";

const METHODS = [
  { id: "bank_transfer", label: "Bank transfer" },
  { id: "check", label: "Check" },
  { id: "venmo", label: "Venmo" },
  { id: "paypal", label: "PayPal" },
  { id: "zelle", label: "Zelle" },
  { id: "cash", label: "Cash" },
  { id: "other", label: "Other" },
];

function money(n, c = "USD") { return `${c === "USD" ? "$" : c + " "}${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }

export default function Reimbursements() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [method, setMethod] = useState("bank_transfer");
  const [ref, setRef] = useState("");
  const [working, setWorking] = useState(false);

  const load = () => listExpenses({ status: "approved" }).then(setItems);
  useEffect(() => { load(); }, []);

  const submit = async (id) => {
    setWorking(true);
    try {
      await markPaid(id, { payment_method: method, payment_reference: ref || null });
      toast({ title: "Marked as reimbursed" });
      setActiveId(null);
      setRef("");
      await load();
    } catch (err) {
      toast({ title: "Could not mark paid", description: err?.response?.data?.detail, variant: "destructive" });
    } finally {
      setWorking(false);
    }
  };

  const total = items.reduce((sum, e) => sum + Number(e.amount), 0);

  return (
    <>
      <PageHeader title="Reimbursements" subtitle="Approved expenses awaiting payment." />
      <PageBody>
        <div className="border border-border bg-card rounded-md mb-6 p-6 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Total to reimburse</div>
            <div className="serif text-3xl mt-1 text-primary mono tabular">{money(total)}</div>
          </div>
          <div className="text-sm text-muted-foreground">{items.length} expense{items.length === 1 ? "" : "s"}</div>
        </div>

        {items.length === 0 ? (
          <div className="border border-dashed border-border rounded-md p-12 text-center">
            <CheckCircle2 className="mx-auto text-secondary" size={32} strokeWidth={1.2} />
            <div className="serif text-xl mt-3 text-primary">All caught up</div>
            <p className="text-sm text-muted-foreground mt-1">No approved expenses are waiting to be reimbursed.</p>
          </div>
        ) : (
          <div className="border border-border bg-card rounded-md divide-y divide-border">
            {items.map((e) => (
              <div key={e.id} className="px-6 py-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{e.vendor}</div>
                    <div className="text-xs text-muted-foreground mt-0.5 truncate">{e.program_name} · {e.submitted_by_name} · {e.spent_on}</div>
                  </div>
                  <div className="text-right">
                    <div className="mono text-sm text-primary tabular">{money(e.amount, e.currency)}</div>
                    <div className="mt-1"><StatusPill status="approved" /></div>
                  </div>
                  <button
                    data-testid={REIMBURSE.markPaidBtn(e.id)}
                    onClick={() => setActiveId(activeId === e.id ? null : e.id)}
                    className="rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90"
                  >
                    {activeId === e.id ? "Cancel" : "Mark as paid"}
                  </button>
                </div>
                {activeId === e.id && (
                  <div className="mt-4 grid sm:grid-cols-3 gap-3 items-end">
                    <label className="block sm:col-span-1">
                      <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Method</span>
                      <select data-testid={REIMBURSE.paymentMethod} value={method} onChange={(ev) => setMethod(ev.target.value)} className="td-input mt-1.5">
                        {METHODS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                      </select>
                    </label>
                    <label className="block sm:col-span-2">
                      <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Reference (optional)</span>
                      <input data-testid={REIMBURSE.paymentRef} value={ref} onChange={(ev) => setRef(ev.target.value)} className="td-input mt-1.5" placeholder="Check #, txn ID, etc." />
                    </label>
                    <button
                      data-testid={REIMBURSE.confirm}
                      onClick={() => submit(e.id)}
                      disabled={working}
                      className="rounded-md bg-status-approved text-white px-4 py-2.5 text-sm font-medium disabled:opacity-60 sm:col-span-3"
                    >
                      {working ? "Working…" : "Confirm reimbursement"}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </PageBody>
      <style>{`
        .td-input { width: 100%; background: hsl(var(--card)); border: 1px solid hsl(var(--border)); border-radius: 6px; padding: 10px 12px; font-size: 14px; color: hsl(var(--foreground)); }
        .td-input:focus { outline: none; border-color: hsl(var(--ring)); box-shadow: 0 0 0 3px hsl(var(--ring) / 0.12); }
      `}</style>
    </>
  );
}
